// src/main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const DEV_LIVE_TRACE_ENABLED = import.meta.env.DEV &&
  (window.location.search.includes('vb_live_trace=1') || localStorage.getItem('vb_live_trace') === '1');
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : (DEV_LIVE_TRACE_ENABLED ? 1 : 0),
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'Could not establish connection',
      'Receiving end does not exist',
    ],
    beforeSend(event) {
      // Drop Chrome extension errors from Sentry entirely
      const msg = event.exception?.values?.[0]?.value ?? '';
      if (msg.includes('Receiving end does not exist') || msg.includes('Could not establish connection')) {
        return null;
      }
      return event;
    },
  });
}

import App from './App';
import './index.css';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { UserProvider } from './contexts/UserContext';
import ErrorBoundary from './components/ErrorBoundary';
import { Toaster } from './lib/toast';

import './lib/firebase';

// 🐛 DEBUG: Conditional provider toggling for debugging infinite loops
// Enable with ?vb_debug=1 or localStorage.setItem('vb_debug', '1')
const DEBUG = import.meta.env.DEV &&
  (window.location.search.includes('vb_debug=1') || localStorage.getItem('vb_debug') === '1');

// Debug flags to disable specific providers (set in browser console)
// window.vbDebugFlags = { disableTheme: true, disableLanguage: true, disableErrorBoundary: true }
const debugFlags = DEBUG ? (window as any).vbDebugFlags || {} : {};

if (DEBUG) {
  console.log('%c🐛 Debug Mode: Provider Toggling Enabled', 'background: #ff6b6b; color: white; padding: 4px 8px; border-radius: 3px;');
  console.log('Set window.vbDebugFlags = { disableTheme: true, disableLanguage: true, disableErrorBoundary: true } to disable providers');
}

// Firebase configuration
// Initialize Firebase
try {
  if (import.meta.env.DEV) {

  }
} catch (error) {
  console.error('❌ Firebase initialization error:', error);
}

// Log Firebase config for development
if (import.meta.env.DEV) {

}

// ✅ Wrap App with UserProvider and Toaster
// 🐛 Conditional wrapping based on debug flags
const AppContent = () => (
  <BrowserRouter>
    <UserProvider>
      <App />
      <Toaster richColors />
    </UserProvider>
  </BrowserRouter>
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {debugFlags.disableErrorBoundary ? (
      debugFlags.disableTheme ? (
        debugFlags.disableLanguage ? (
          <AppContent />
        ) : (
          <LanguageProvider>
            <AppContent />
          </LanguageProvider>
        )
      ) : (
        <ThemeProvider>
          {debugFlags.disableLanguage ? (
            <AppContent />
          ) : (
            <LanguageProvider>
              <AppContent />
            </LanguageProvider>
          )}
        </ThemeProvider>
      )
    ) : (
      <ErrorBoundary>
        {debugFlags.disableTheme ? (
          debugFlags.disableLanguage ? (
            <AppContent />
          ) : (
            <LanguageProvider>
              <AppContent />
            </LanguageProvider>
          )
        ) : (
          <ThemeProvider>
            {debugFlags.disableLanguage ? (
              <AppContent />
            ) : (
              <LanguageProvider>
                <AppContent />
              </LanguageProvider>
            )}
          </ThemeProvider>
        )}
      </ErrorBoundary>
    )}
  </React.StrictMode>
);
